/**
 * 设备管理相关类型定义
 * v8.0 新增：IoT 设备管理功能
 */

/**
 * 设备状态
 */
export type DeviceStatus = 'unused' | 'active' | 'blocked'

/**
 * 设备权限
 */
export interface DevicePermissions {
  wss: boolean  // 语音通信（WebSocket）
  sse: boolean  // 文字通信（Server-Sent Events）
  server_vad?: boolean  // v8.3: 云端 VAD（收费功能）
}

/**
 * 设备信息
 */
export interface Device {
  device_id: string
  device_secret?: string  // 仅创建/重置时返回
  project_id: string
  order_no: string
  batch_no: string
  status: DeviceStatus
  permissions: DevicePermissions
  name?: string
  current_token?: string
  token_expires_at?: string
  created_at: string
  updated_at: string
  activated_at?: string
  last_connected_at?: string
}

/**
 * 设备列表统计
 */
export interface DeviceStats {
  total: number
  active: number
  unused: number
  blocked: number
}

/**
 * 设备列表响应
 */
export interface DeviceListResponse {
  devices: Device[]
  total: number
  page: number
  size: number
  stats: DeviceStats
}

/**
 * 设备列表查询参数
 */
export interface DeviceListParams {
  order_no?: string
  status?: DeviceStatus
  page?: number
  size?: number
}

/**
 * 批量生成设备请求
 */
export interface DeviceBatchCreateRequest {
  order_no: string
  count: number
  device_id_prefix?: string
  permissions?: DevicePermissions
  expires_in_days?: number
}

/**
 * 批量生成设备响应
 */
export interface DeviceBatchCreateResponse {
  batch_no: string
  order_no: string
  count: number
  download_url: string
}

/**
 * 更新设备请求
 */
export interface DeviceUpdateRequest {
  name?: string
  permissions?: DevicePermissions
}

/**
 * 延长有效期请求
 */
export interface DeviceExtendRequest {
  extend_days: number
}

/**
 * 批量操作请求
 */
export interface DeviceBatchOperationRequest {
  order_no: string
}

/**
 * 批量操作响应
 */
export interface DeviceBatchOperationResponse {
  affected_count: number
  order_no: string
}

/**
 * 重置密钥响应
 */
export interface DeviceResetSecretResponse {
  device_id: string
  device_secret: string
  message: string
}

/**
 * 设备测试 Token 响应
 * v8.2 新增：设备可用性测试功能
 */
export interface DeviceTestTokenResponse {
  token: string
  device_id: string
  project_id: string
  expires_at: string
  permissions: DevicePermissions
  is_test: boolean
}

/**
 * 设备状态显示配置
 */
export const DEVICE_STATUS_CONFIG: Record<DeviceStatus, { label: string; color: string; icon: string }> = {
  active: { label: '正常', color: 'green', icon: '✅' },
  unused: { label: '未激活', color: 'yellow', icon: '⏳' },
  blocked: { label: '已吊销', color: 'red', icon: '🚫' },
}

/**
 * 获取权限显示文本
 */
export function getPermissionLabel(permissions: DevicePermissions): string {
  const parts: string[] = []
  if (permissions.wss && permissions.sse) {
    parts.push('全部')
  } else if (permissions.wss) {
    parts.push('仅 WSS')
  } else if (permissions.sse) {
    parts.push('仅 SSE')
  } else {
    parts.push('无')
  }
  // v8.3: 云端 VAD 标记
  if (permissions.server_vad) {
    parts.push('+ 云端VAD')
  }
  return parts.join(' ')
}

/**
 * 有效期选项
 */
export const EXPIRES_OPTIONS = [
  { value: 90, label: '90 天' },
  { value: 180, label: '180 天' },
  { value: 365, label: '1 年' },
  { value: 730, label: '2 年' },
  { value: 1095, label: '3 年' },
]

/**
 * 批量生成最大数量
 */
export const MAX_BATCH_COUNT = 5000
